import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { catchError, finalize, switchMap } from 'rxjs/operators';
import { FaceCompareService } from '../../../../services/face-compare/face-compare.service';
import { SearchHistoryService } from '../../../../services/search-history/search-history.service';
import { FaceCompareLoaderComponent } from '../face-compare-loader/face-compare-loader.component';
import { FaceCompareFaceComparisonSliderComponent } from './face-compare-face-comparison-slider/face-compare-face-comparison-slider.component';
import { FaceCompareImageComparisonSliderComponent } from './face-compare-image-comparison-slider/face-compare-image-comparison-slider.component';

@Component({
  selector: 'app-face-compare-face-detector',
  imports: [FormsModule, CommonModule, FaceCompareLoaderComponent],
  templateUrl: './face-compare-face-detector.component.html',
  styleUrl: './face-compare-face-detector.component.css',
})
export class FaceCompareFaceDetectorComponent implements OnInit, OnDestroy {
  selectedImages: any[] = [];

  @ViewChild('image1') image1!: ElementRef<HTMLImageElement>;
  @ViewChild('image2') image2!: ElementRef<HTMLImageElement>;

  image1Zoom = 1;
  image2Zoom = 1;

  image1PanX = 0;
  image1PanY = 0;
  image2PanX = 0;
  image2PanY = 0;

  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private currentImage = 0;

  isLoading = false;
  isDetectingFaces = false;
  isComparingFaces = false;
  isEnhancingImage = false;
  isStaleResult = false;
  isUpdatingNotes = false;
  faceCompareId: string | null = null;
  notes = '';
  readonly notesMaxLength = 300;
  faceDetectionResults: any[] = [];
  processedImages: string[] = [];
  enhancedImages: string[] = [];
  selectedFaces: { [key: string]: boolean } = {};
  faceImages: { [key: string]: string } = {};
  comparisonResults: any = null;
  private subscription: Subscription = new Subscription();

  constructor(
    private faceCompareService: FaceCompareService,
    private searchHistoryService: SearchHistoryService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const navState = history.state || {};
    const faceCompareIdFromQuery =
      this.route.snapshot.queryParamMap.get('face_compare_id');
    const faceCompareId =
      faceCompareIdFromQuery || navState.faceCompareId || null;
    const faceCompareHistory = navState.faceCompareHistory || null;

    if (faceCompareId) {
      this.isStaleResult = true;
      this.faceCompareId = faceCompareId;
      this.syncFaceCompareIdInUrl(faceCompareId);
      this.hydrateStaleCompareResult(faceCompareId, faceCompareHistory);
    } else {
      this.selectedImages = this.faceCompareService.getSelectedImages();
      this.getFacesFromImages();
    }

    this.subscription.add(
      this.faceCompareService.compareFacesTrigger$.subscribe(() => {
        this.performFaceComparison();
      }),
    );

    this.subscription.add(
      this.faceCompareService.splitViewDialogTrigger$.subscribe(() => {
        this.openSplitViewDialog();
      }),
    );

    this.subscription.add(
      this.faceCompareService.splitFacesTrigger$.subscribe(() => {
        this.handleSplitFaces();
      }),
    );
  }

  /**
   * Reopen a saved compare using history images + stored compare_payload
   * so the existing comparison-results table renders (no JSON overlay).
   * History row may be missing on hard refresh — then we re-fetch by id.
   */
  private hydrateStaleCompareResult(
    faceCompareId: string,
    historyRow: any,
  ): void {
    this.isComparingFaces = true;

    const history$ = historyRow?.image_a_url
      ? of(historyRow)
      : this.searchHistoryService
          .getFaceCompareHistories(1, 1, 'face_compare_id', faceCompareId)
          .pipe(
            catchError(() => of(null)),
            switchMap((response) =>
              of(response?.data?.data_list?.[0] || null),
            ),
          );

    history$
      .pipe(
        switchMap((row) => {
          this.applyHistoryImages(row);
          this.notes = String(row?.notes || '').trim() || 'Face Compare';
          return this.searchHistoryService
            .getFaceCompareResults(faceCompareId, 1, 10)
            .pipe(catchError(() => of(null)));
        }),
        finalize(() => {
          this.isComparingFaces = false;
        }),
      )
      .subscribe((response) => {
        const first = response?.data?.data_list?.[0];
        if (!first?.compare_payload) {
          this.toastr.warning(
            'No stored comparison payload found for this history item.',
          );
          return;
        }

        this.comparisonResults = first.compare_payload;
        this.seedFaceImagesFromExtraData(
          first.extra_data,
          first.compare_payload,
        );
      });
  }

  private applyHistoryImages(historyRow: any): void {
    const imageAUrl =
      historyRow?.image_a_url ||
      'https://placehold.co/400x400?text=Image+A';
    const imageBUrl =
      historyRow?.image_b_url ||
      'https://placehold.co/400x400?text=Image+B';

    this.selectedImages = [
      {
        id: 'stale-a',
        name: historyRow?.image_a_file_name || 'Image A',
        url: imageAUrl,
        selected: true,
      },
      {
        id: 'stale-b',
        name: historyRow?.image_b_file_name || 'Image B',
        url: imageBUrl,
        selected: true,
      },
    ];
    this.faceCompareService.setSelectedImages(this.selectedImages);
    this.processedImages = [imageAUrl, imageBUrl];
    this.faceDetectionResults = [
      { detected_faces: [], image_id: null },
      { detected_faces: [], image_id: null },
    ];
  }

  private syncFaceCompareIdInUrl(faceCompareId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { face_compare_id: faceCompareId },
      replaceUrl: true,
    });
  }

  onUpdateNotesButtonClick(): void {
    if (!this.faceCompareId) {
      this.toastr.info('Save a compare first before adding notes.');
      return;
    }

    const trimmed = (this.notes || '').trim();
    this.isUpdatingNotes = true;
    this.searchHistoryService
      .updateFaceCompareNotes(this.faceCompareId, trimmed)
      .pipe(
        finalize(() => {
          this.isUpdatingNotes = false;
        }),
      )
      .subscribe({
        next: () => {
          this.notes = trimmed;
          this.toastr.success('Notes updated');
        },
        error: (error) => {
          console.error(error);
          this.toastr.error(
            error?.error?.message_key ||
              error?.error?.message ||
              'Failed to update notes',
          );
        },
      });
  }

  private seedFaceImagesFromExtraData(
    extraData: any,
    comparePayload: any,
  ): void {
    if (!extraData && !comparePayload) {
      return;
    }

    const cropA = extraData?.crop_a_url;
    const cropB = extraData?.crop_b_url;
    const facesA = Array.isArray(extraData?.faces_a) ? extraData.faces_a : [];
    const facesB = Array.isArray(extraData?.faces_b) ? extraData.faces_b : [];

    if (cropA && facesA[0]?.face_id) {
      this.faceImages[facesA[0].face_id] = cropA;
    }
    if (cropB && facesB[0]?.face_id) {
      this.faceImages[facesB[0].face_id] = cropB;
    }

    // Fallback: map any face_ids referenced in compare_payload to available crops
    const rows = Array.isArray(comparePayload?.results)
      ? comparePayload.results
      : [];
    for (const row of rows) {
      const queryFaceId = row?.query?.face_id;
      if (queryFaceId && cropA && !this.faceImages[queryFaceId]) {
        this.faceImages[queryFaceId] = cropA;
      }
      const matches = Array.isArray(row?.matches) ? row.matches : [];
      for (const match of matches) {
        const matchFaceId = match?.face_id;
        if (matchFaceId && cropB && !this.faceImages[matchFaceId]) {
          this.faceImages[matchFaceId] = cropB;
        }
      }
    }

    // If crops exist but face ids are missing, still show crops via placeholder keys
    // used only when getFaceImageById falls through — prefer real ids above.
    if (cropA && Object.keys(this.faceImages).length === 0) {
      this.faceImages['crop_a'] = cropA;
    }
    if (cropB && !this.faceImages['crop_b'] && facesB.length === 0) {
      this.faceImages['crop_b'] = cropB;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openSplitViewDialog(): void {
    const dialogRef = this.dialog.open(
      FaceCompareImageComparisonSliderComponent,
      {
        width: 'calc(100vw - 3rem)',
        height: 'calc(100vh - 3rem)',
        maxWidth: 'calc(100vw - 3rem)',
        maxHeight: 'calc(100vh - 3rem)',
        panelClass: 'image-comparison-dialog',
        data: {
          selectedImages: this.selectedImages,
          processedImages: this.processedImages,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {});
  }

  get currentLoadingState(): { isLoading: boolean; loadingText: string } {
    if (this.isDetectingFaces) {
      return { isLoading: true, loadingText: 'Detecting faces...' };
    }
    if (this.isComparingFaces) {
      return { isLoading: true, loadingText: 'Comparing faces...' };
    }
    if (this.isEnhancingImage) {
      return { isLoading: true, loadingText: 'Enhancing image...' };
    }
    return { isLoading: false, loadingText: '' };
  }

  performFaceComparison(): void {
    const payload = this.preparePerformFaceComparisonPayload();
    if (!payload) {
      return;
    }

    this.isComparingFaces = true;
    this.faceCompareService
      .getFaceComparisonResults(payload)
      .pipe(
        catchError((error) => {
          return of(null);
        }),
        finalize(() => {
          this.isComparingFaces = false;
        }),
      )
      .subscribe((results) => {
        if (!results?.data) {
          return;
        }

        this.comparisonResults = results.data;
        const savedId = results.data?.face_compare_id;
        if (savedId) {
          this.faceCompareId = savedId;
          this.notes =
            String(results.data?.notes || '').trim() ||
            this.notes ||
            'Face Compare';
          this.syncFaceCompareIdInUrl(savedId);
        }
      });
  }

  private preparePerformFaceComparisonPayload(): any | null {
    if (!this.selectedImages || this.selectedImages.length < 2) {
      return null;
    }

    if (!this.faceDetectionResults || this.faceDetectionResults.length < 2) {
      return null;
    }

    const facesA = this.getSelectedFacesForImage(0);
    const facesB = this.getSelectedFacesForImage(1);

    if (facesA.length === 0 || facesB.length === 0) {
      return null;
    }

    const imageAId = this.faceDetectionResults[0].image_id;
    const imageBId = this.faceDetectionResults[1].image_id;

    if (!imageAId || !imageBId) {
      return null;
    }

    const imageA = this.selectedImages[0];
    const imageB = this.selectedImages[1];

    const payload: any = {
      image_a_id: imageAId,
      image_b_id: imageBId,
      faces_a: facesA.map((face) => ({
        image_id: imageAId,
        face_id: face.fid,
      })),
      faces_b: facesB.map((face) => ({
        image_id: imageBId,
        face_id: face.fid,
      })),
    };

    // Persistence fields (history skipped by backend if either image URI is missing)
    if (imageA?.url) {
      payload.image_a = imageA.url;
    }
    if (imageB?.url) {
      payload.image_b = imageB.url;
    }
    if (imageA?.name) {
      payload.image_a_file_name = imageA.name;
    }
    if (imageB?.name) {
      payload.image_b_file_name = imageB.name;
    }

    const faceCropA = this.faceImages[facesA[0]?.fid];
    const faceCropB = this.faceImages[facesB[0]?.fid];
    if (faceCropA) {
      payload.face_crop_a = faceCropA;
    }
    if (faceCropB) {
      payload.face_crop_b = faceCropB;
    }

    const { ip, location } = this.retrieveIPAndLocation();
    if (ip) {
      payload.ip_address = ip;
    }
    if (location) {
      payload.location = location;
    }
    payload.device = 'Chrome';

    return payload;
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    const ip = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';
    return { ip, location };
  }

  private getSelectedFacesForImage(imageIndex: number): any[] {
    if (!this.faceDetectionResults || !this.faceDetectionResults[imageIndex]) {
      return [];
    }

    const faces = this.faceDetectionResults[imageIndex].detected_faces || [];
    return faces.filter((face: any) => this.selectedFaces[face.fid]);
  }

  getFacesFromImages(): void {
    if (!this.selectedImages || this.selectedImages.length === 0) {
      return;
    }

    this.isDetectingFaces = true;
    this.faceDetectionResults = [];
    this.comparisonResults = null;

    const faceDetectionObservables = this.selectedImages.map((image) => {
      const base64Data = image.url;
      return this.faceCompareService.getFacesFromImage(base64Data).pipe(
        catchError(() => of(null)),
      );
    });

    forkJoin(faceDetectionObservables)
      .pipe(
        finalize(() => {
          this.isDetectingFaces = false;
        }),
      )
      .subscribe((results) => {
        const anySuccess = results.some((result) => result !== null);
        if (!anySuccess) {
          this.toastr.error(
            'Face detection failed. Check backend Face API configuration and try again.',
          );
        }

        // Keep index alignment with selectedImages (null → empty faces)
        this.faceDetectionResults = results.map(
          (result) => result?.data || { detected_faces: [] },
        );
        this.processImagesWithBoundingBoxes();
      });
  }

  getFacesForImage(imageIndex: number): any[] {
    if (this.faceDetectionResults && this.faceDetectionResults[imageIndex]) {
      return this.faceDetectionResults[imageIndex].detected_faces || [];
    }
    return [];
  }

  getImageWithBoundingBoxes(imageIndex: number): string {
    if (this.enhancedImages[imageIndex]) {
      return this.enhancedImages[imageIndex];
    }
    return (
      this.processedImages[imageIndex] ||
      this.selectedImages[imageIndex]?.url ||
      ''
    );
  }

  getFaceImage(imageIndex: number, face: any): string {
    const faceId = face.fid;
    if (this.faceImages[faceId]) {
      return this.faceImages[faceId];
    }

    this.extractFaceImage(imageIndex, face);
    return '';
  }

  getFaceImageById(imageId: string, faceId: string): string {
    if (this.faceImages[faceId]) {
      return this.faceImages[faceId];
    }

    const imageIndex = this.faceDetectionResults.findIndex(
      (result) => result.image_id === imageId,
    );
    if (imageIndex !== -1) {
      const faces = this.faceDetectionResults[imageIndex].detected_faces || [];
      const face = faces.find((f: any) => f.fid === faceId);
      if (face) {
        this.extractFaceImage(imageIndex, face);
      }
    }

    if (this.isStaleResult) {
      return 'https://placehold.co/120x120?text=Face';
    }
    return '';
  }

  private extractFaceImage(imageIndex: number, face: any): void {
    const image = this.selectedImages[imageIndex];
    if (!image?.url || !face?.bbox) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      canvas.width = face.bbox.width;
      canvas.height = face.bbox.height;
      ctx.drawImage(
        img,
        face.bbox.x,
        face.bbox.y,
        face.bbox.width,
        face.bbox.height,
        0,
        0,
        face.bbox.width,
        face.bbox.height,
      );

      this.faceImages[face.fid] = canvas.toDataURL('image/png');
    };

    img.onerror = () => {
      this.faceImages[face.fid] = '';
    };

    img.src = image.url;
  }

  onFaceClick(face: any): void {
    const faceId = face.fid;
    this.selectedFaces[faceId] = !this.selectedFaces[faceId];
  }

  isFaceSelected(face: any): boolean {
    return this.selectedFaces[face.fid] || false;
  }

  processImagesWithBoundingBoxes(): void {
    this.processedImages = [];

    this.selectedImages.forEach((image, index) => {
      if (!image?.url) {
        this.processedImages[index] = '';
        return;
      }

      const faces = this.getFacesForImage(index);
      if (!faces || faces.length === 0) {
        this.processedImages[index] = image.url;
        return;
      }

      this.drawBoundingBoxesOnImage(
        image.url,
        index,
        faces,
        (result: string) => {
          this.processedImages[index] = result;
        },
      );
    });
  }

  private drawBoundingBoxesOnImage(
    imageUrl: string,
    imageIndex: number,
    faces: any[],
    callback: (result: string) => void,
  ): void {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        callback(imageUrl);
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const baseSize = Math.min(img.width, img.height);
      const borderWidth = Math.max(2, Math.floor(baseSize / 200));
      const fontSize = Math.max(12, Math.floor(baseSize / 30));
      const padding = Math.max(2, Math.floor(baseSize / 150));
      const labelHeight = Math.max(16, Math.floor(baseSize / 25));
      ctx.strokeStyle = '#22c063';
      ctx.lineWidth = borderWidth;
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = '#22c063';
      ctx.textBaseline = 'top';

      faces.forEach((face: any) => {
        const bbox = face.bbox;
        const confidence = (face.confidence * 100).toFixed(0) + '%';

        ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);

        const textWidth = ctx.measureText(confidence).width;
        ctx.fillStyle = '#22c063';
        ctx.fillRect(
          bbox.x - padding,
          bbox.y - labelHeight - padding,
          textWidth + padding * 2,
          labelHeight,
        );
        ctx.fillStyle = '#000000';
        ctx.fillText(
          confidence,
          bbox.x + padding,
          bbox.y - labelHeight + padding / 2,
        );
      });

      callback(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      callback(imageUrl);
    };

    img.src = imageUrl;
  }

  onZoomChange(imageNumber: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const zoom = parseFloat(target.value);

    if (imageNumber === 1) {
      this.image1Zoom = zoom;
      if (zoom <= 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.constrainPan(1);
      }
    } else {
      this.image2Zoom = zoom;
      if (zoom <= 1) {
        this.image2PanX = 0;
        this.image2PanY = 0;
      } else {
        this.constrainPan(2);
      }
    }
  }

  onWheel(event: WheelEvent, imageNumber: number): void {
    event.preventDefault();
    this.updateZoomLevelByWheel(event, imageNumber);
  }

  updateZoomLevelByWheel(event: WheelEvent, imageNumber: number): void {
    const currentZoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;
    const newZoom = this.calculateNewZoom(currentZoom, event.deltaY, 0.1);

    this.updateImageZoomLevel(imageNumber, newZoom);
  }

  private calculateNewZoom(
    currentZoom: number,
    deltaY: number,
    zoomStep: number,
  ): number {
    let newZoom: number;

    if (deltaY < 0) {
      newZoom = Math.min(10, currentZoom + zoomStep);
    } else {
      newZoom = Math.max(0.5, currentZoom - zoomStep);
    }

    return Math.round(newZoom * 10) / 10;
  }

  private updateImageZoomLevel(imageNumber: number, newZoom: number): void {
    if (imageNumber === 1) {
      this.image1Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    } else {
      this.image2Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    }
  }

  private resetPanForZoom(newZoom: number, imageNumber: number): void {
    if (newZoom <= 1) {
      if (imageNumber === 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.image2PanX = 0;
        this.image2PanY = 0;
      }
    } else {
      this.constrainPan(imageNumber);
    }
  }

  onReset(imageNumber: number): void {
    const imageIndex = imageNumber - 1;

    if (imageNumber === 1) {
      this.image1Zoom = 1;
      this.image1PanX = 0;
      this.image1PanY = 0;
    } else {
      this.image2Zoom = 1;
      this.image2PanX = 0;
      this.image2PanY = 0;
    }

    this.enhancedImages[imageIndex] = '';
  }

  onEnhance(imageNumber: number): void {
    this.enhanceImage(imageNumber);
  }

  enhanceImage(imageNumber: number): void {
    const imageIndex = imageNumber - 1;

    if (!this.selectedImages[imageIndex]?.url) {
      this.toastr.error('No image available to enhance');
      return;
    }

    const imageBase64 = this.selectedImages[imageIndex].url;

    this.isEnhancingImage = true;
    this.faceCompareService
      .getEnhancedImage(imageBase64)
      .pipe(
        catchError((error) => {
          this.toastr.error('Failed to enhance image');
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response && response.data) {
          let enhancedImageData = response.data;
          if (!enhancedImageData.startsWith('data:image')) {
            enhancedImageData = `data:image/png;base64,${enhancedImageData}`;
          }

          this.getFacesFromEnhancedImage(enhancedImageData, imageIndex);
        } else {
          this.isEnhancingImage = false;
        }
      });
  }

  getFacesFromEnhancedImage(
    enhancedImageData: string,
    imageIndex: number,
  ): void {
    this.faceCompareService
      .getFacesFromImage(enhancedImageData)
      .pipe(
        catchError((error) => {
          this.toastr.error('Failed to detect faces on enhanced image');
          return of(null);
        }),
        finalize(() => {
          this.isEnhancingImage = false;
        }),
      )
      .subscribe((faceDetectionResponse) => {
        if (faceDetectionResponse && faceDetectionResponse.data) {
          this.faceDetectionResults[imageIndex] = faceDetectionResponse.data;

          this.selectedImages[imageIndex].url = enhancedImageData;

          const faces = this.getFacesForImage(imageIndex);
          if (faces && faces.length > 0) {
            this.drawBoundingBoxesOnImage(
              enhancedImageData,
              imageIndex,
              faces,
              (result: string) => {
                this.enhancedImages[imageIndex] = result;
                this.processedImages[imageIndex] = result;
              },
            );
          } else {
            this.enhancedImages[imageIndex] = enhancedImageData;
            this.processedImages[imageIndex] = enhancedImageData;
          }
        } else {
          this.enhancedImages[imageIndex] = enhancedImageData;
          this.processedImages[imageIndex] = enhancedImageData;
        }
      });
  }

  startPan(event: MouseEvent, imageNumber: number): void {
    if (event.button === 0) {
      this.isPanning = true;
      this.currentImage = imageNumber;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
    }
  }

  onPan(event: MouseEvent, imageNumber: number): void {
    if (this.isPanning && this.currentImage === imageNumber) {
      const deltaX = event.clientX - this.panStartX;
      const deltaY = event.clientY - this.panStartY;

      if (imageNumber === 1) {
        this.image1PanX += deltaX;
        this.image1PanY += deltaY;
        this.constrainPan(1);
      } else {
        this.image2PanX += deltaX;
        this.image2PanY += deltaY;
        this.constrainPan(2);
      }

      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
    }
  }

  endPan(imageNumber: number): void {
    if (this.isPanning && this.currentImage === imageNumber) {
      this.isPanning = false;
      this.currentImage = 0;
    }
  }

  private constrainPan(imageNumber: number): void {
    const image =
      imageNumber === 1
        ? this.image1?.nativeElement
        : this.image2?.nativeElement;
    const zoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;

    if (!image) return;

    const container = image.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    const scaledWidth = imageRect.width * zoom;
    const scaledHeight = imageRect.height * zoom;
    const maxPanX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxPanY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    if (imageNumber === 1) {
      this.image1PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image1PanX));
      this.image1PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image1PanY));
    } else {
      this.image2PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image2PanX));
      this.image2PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image2PanY));
    }
  }

  private handleSplitFaces(): void {
    const facesFromImage1 = this.getSelectedFacesForImage(0);
    const facesFromImage2 = this.getSelectedFacesForImage(1);

    if (facesFromImage1.length !== 1 || facesFromImage2.length !== 1) {
      this.toastr.error(
        'Please select exactly one face from each face list below',
      );
      return;
    }

    this.openFaceComparisonDialog(facesFromImage1[0], facesFromImage2[0]);
  }

  private openFaceComparisonDialog(face1: any, face2: any): void {
    const dialogRef = this.dialog.open(
      FaceCompareFaceComparisonSliderComponent,
      {
        width: 'calc(100vw - 3rem)',
        height: 'calc(100vh - 3rem)',
        maxWidth: 'calc(100vw - 3rem)',
        maxHeight: 'calc(100vh - 3rem)',
        panelClass: 'face-comparison-dialog',
        data: {
          selectedImages: this.selectedImages,
          selectedFaces: [face1, face2],
        },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {});
  }

  compareFaces(): void {
    this.faceCompareService.triggerFaceComparison();
  }

  splitFaces(): void {
    this.faceCompareService.triggerSplitFaces();
  }
}
